import {
  DIET_CHEER_KINDS,
  dietCalorieTotalOf,
  dietMealEntriesOf,
  type DietCheerKind,
  type DietLog,
} from "@couples/shared";
import { Flame, Heart, Pencil, ThumbsUp, Trash2, Utensils, type LucideIcon } from "lucide-react";

const CHEER_ICONS: Record<DietCheerKind, LucideIcon> = {
  thumbs: ThumbsUp,
  flame: Flame,
  heart: Heart,
};

export interface DietDayEntry {
  log: DietLog;
  name: string;
  color: string;
  isSelf: boolean;
  /** 開始からの増減(kg)。目標も過去の記録も無ければ null */
  delta: number | null;
  /** この記録に押されている応援。自分の記録なら「もらったぶん」、相手の記録なら「自分が押したぶん」 */
  cheers: DietCheerKind[];
}

interface Props {
  /** 見出しに出す日付の読み(「今日」「昨日」「8/25」) */
  dayLabel: string;
  entries: DietDayEntry[];
  busyLogId: string | null;
  onCheer: (log: DietLog, kind: DietCheerKind) => void;
  onEdit: (log: DietLog) => void;
  onDelete: (log: DietLog) => void;
}

function signed(value: number): string {
  if (value > 0) return `+${value.toFixed(1)}`;
  if (value < 0) return `-${Math.abs(value).toFixed(1)}`;
  return "±0.0";
}

/**
 * 1日ぶんの記録カード。二人の行を同じ日の中に並べる。
 *
 * 応援スタンプは相手の記録にだけ押せる。自分の記録の側は、もらったぶんだけを
 * 押せない印として出す(自分で自分を褒められると、数字の意味が消えるため)。
 */
export default function DietDayCard({
  dayLabel,
  entries,
  busyLogId,
  onCheer,
  onEdit,
  onDelete,
}: Props) {
  return (
    <div className="rounded-xl border border-[var(--color-line)] bg-white p-4">
      <span className="text-[11px] text-[var(--color-sub)]">{dayLabel}</span>

      <div className="mt-2 flex flex-col gap-3.5">
        {entries.map(({ log, name, color, isSelf, delta, cheers }) => (
          <div key={log.id} className="flex gap-2.5">
            <span
              className="mt-1.5 size-2 shrink-0 rounded-full"
              style={{ background: color }}
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-semibold">{name}</span>
                <span className="text-xs tabular-nums text-[var(--color-sub)]">
                  {log.weight.toFixed(1)}kg
                </span>
                {delta !== null && (
                  <span className="text-xs tabular-nums" style={{ color }}>
                    {signed(delta)}
                  </span>
                )}
                {isSelf && (
                  <span className="ml-auto flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        onEdit(log);
                      }}
                      disabled={busyLogId === log.id}
                      aria-label="この記録を直す"
                      className="-m-3 flex size-11 items-center justify-center rounded-full transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] disabled:opacity-50"
                    >
                      <Pencil className="size-4 text-[var(--color-sub)]" aria-hidden />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        onDelete(log);
                      }}
                      disabled={busyLogId === log.id}
                      aria-label="この記録を消す"
                      className="-m-3 flex size-11 items-center justify-center rounded-full transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] disabled:opacity-50"
                    >
                      <Trash2 className="size-4 text-[var(--color-sub)]" aria-hidden />
                    </button>
                  </span>
                )}
              </div>

              {log.note !== null && <p className="mt-1 text-sm leading-relaxed">{log.note}</p>}

              <div className="mt-1.5 flex flex-col gap-0.5">
                {dietMealEntriesOf(log).map((entry) => (
                  <span
                    key={entry.label || "legacy"}
                    className="flex items-center gap-1.5 text-[11px] text-[var(--color-sub)]"
                  >
                    <Utensils className="size-3 shrink-0" aria-hidden />
                    {entry.label !== "" ? `${entry.label}: ${entry.text}` : entry.text}
                  </span>
                ))}
                {log.exercise !== null && (
                  <span className="flex items-center gap-1.5 text-[11px] text-[var(--color-sub)]">
                    <Flame className="size-3 shrink-0" aria-hidden />
                    {log.exercise}
                  </span>
                )}
                {log.bodyFat !== null && (
                  <span className="text-[11px] tabular-nums text-[var(--color-sub)]">
                    体脂肪率 {log.bodyFat.toFixed(1)}%
                  </span>
                )}
                {dietCalorieTotalOf(log) !== null && (
                  <span className="text-[11px] tabular-nums text-[var(--color-sub)]">
                    摂取カロリー {dietCalorieTotalOf(log)}kcal
                  </span>
                )}
              </div>

              {/* 自分の記録でまだ何ももらっていない日は、空の行ぶんの余白も作らない */}
              {(!isSelf || cheers.length > 0) && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {DIET_CHEER_KINDS.map(({ value, label }) => {
                    const active = cheers.includes(value);
                    // 自分の記録には押せない。もらっていない種類は出さない
                    if (isSelf && !active) return null;
                    const Icon = CHEER_ICONS[value];
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          onCheer(log, value);
                        }}
                        disabled={isSelf || busyLogId === log.id}
                        aria-pressed={isSelf ? undefined : active}
                        className={`flex items-center gap-1 rounded-full px-2 py-1 text-[11px] transition-colors disabled:opacity-100 ${
                          active
                            ? "text-white"
                            : "border border-[var(--color-line)] text-[var(--color-sub)] hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)]"
                        }`}
                        style={active ? { background: color } : undefined}
                      >
                        <Icon className="size-3" aria-hidden />
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
